/**
 * Bouton "Vos réactions" - Point d'entrée du module feedback
 *
 * Position: fixed, coin inférieur droit
 * Couleur: violet (distinct des actions principales bleues)
 * Animation: pulse discret toutes les 30s (cesse après 1er feedback)
 */

import { useState, useEffect, useCallback } from 'react';
import FeedbackPanel from './FeedbackPanel';

// Feature flag frontend
const FEATURE_ENABLED = import.meta.env.VITE_FEATURE_FEEDBACK_ENABLED !== 'false';

export interface FeedbackButtonProps {
  /** Classe CSS additionnelle */
  className?: string;
}

export function FeedbackButton({ className = '' }: FeedbackButtonProps) {
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [hasSentFeedback, setHasSentFeedback] = useState(false);
  const [shouldPulse, setShouldPulse] = useState(false);

  // Animation pulse toutes les 30 secondes (si pas encore envoyé de feedback)
  useEffect(() => {
    if (hasSentFeedback) {
      setShouldPulse(false);
      return;
    }

    // Pulse initial après 5 secondes
    const initialTimeout = setTimeout(() => {
      setShouldPulse(true);
      // Arrêter le pulse après 2 secondes
      setTimeout(() => setShouldPulse(false), 2000);
    }, 5000);

    // Pulse toutes les 30 secondes
    const interval = setInterval(() => {
      setShouldPulse(true);
      setTimeout(() => setShouldPulse(false), 2000);
    }, 30000);

    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
    };
  }, [hasSentFeedback]);

  const handleOpenPanel = useCallback(() => {
    setIsPanelOpen(true);
  }, []);

  const handleClosePanel = useCallback(() => {
    setIsPanelOpen(false);
  }, []);

  const handleFeedbackSent = useCallback(() => {
    setHasSentFeedback(true);
  }, []);

  // Si feature flag désactivé, ne rien rendre
  if (!FEATURE_ENABLED) {
    return null;
  }

  return (
    <>
      {/* Bouton flottant */}
      <button
        type="button"
        onClick={handleOpenPanel}
        aria-label="Ouvrir le formulaire de retours utilisateur"
        aria-expanded={isPanelOpen}
        aria-haspopup="dialog"
        className={`
          flex items-center gap-2
          px-3 py-2
          bg-violet-600 hover:bg-violet-700
          text-white font-medium text-sm
          rounded-lg shadow-sm
          transition-all duration-200
          focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2
          ${isPanelOpen ? 'bg-violet-700' : ''}
          ${shouldPulse ? 'animate-pulse' : ''}
          ${className}
        `}
      >
        {/* Icône bulle de commentaire */}
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
          />
        </svg>
        <span>Vos réactions</span>
      </button>

      {/* Panneau de feedback */}
      <FeedbackPanel
        isOpen={isPanelOpen}
        onClose={handleClosePanel}
        onFeedbackSent={handleFeedbackSent}
      />
    </>
  );
}

export default FeedbackButton;
